import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Zap, PencilLine, Lock, ArrowRight, Loader2 } from "lucide-react";
import PlaidLinkButton from "@/components/PlaidLinkButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LinkCardChooserDialog
 *
 * Signed-in "Add a credit card" chooser. When Plaid is configured on the
 * backend, Automatic Sync opens the Plaid Link widget; otherwise it stays
 * gated as "Coming soon". Manual entry is always available.
 */
const LinkCardChooserDialog = ({ open, onOpenChange, onChooseManual, onLinked }) => {
  const [plaidEnabled, setPlaidEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!open) return () => {};
    setLoading(true);
    axios
      .get(`${API}/plaid/status`, { withCredentials: true })
      .then((res) => {
        if (!cancelled) setPlaidEnabled(Boolean(res.data?.enabled));
      })
      .catch(() => {
        if (!cancelled) setPlaidEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handlePlaidLinked = (res) => {
    onOpenChange(false);
    if (onLinked) onLinked(res);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#0B1226]/95 backdrop-blur-xl border border-white/10 text-slate-100 p-6"
        data-testid="link-card-chooser-dialog"
      >
        <DialogTitle
          className="text-xl font-bold text-white"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Add a credit card
        </DialogTitle>
        <DialogDescription className="text-sm text-slate-300 mt-1">
          Two ways to keep CardWatch up to date. Pick what suits you.
        </DialogDescription>

        <div className="mt-5 space-y-3">
          {/* Automatic Sync (Plaid) — gated on server status */}
          {plaidEnabled ? (
            <div
              data-testid="link-card-chooser-automatic"
              className="w-full text-left rounded-2xl border border-[#00E676]/30 bg-[#00E676]/[0.05] p-4"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#00E676]/15 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0">
                  <Zap size={18} className="text-[#00E676]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-sm font-semibold text-white"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      Automatic Sync
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#00E676]/40 bg-[#00E676]/[0.08] px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] text-[#00E676] font-bold">
                      Powered by Plaid
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed mb-3">
                    Live balance + limit updates from your bank. Read-only. Supports UK, EU, US and Canada institutions.
                  </p>
                  <PlaidLinkButton
                    onLinked={handlePlaidLinked}
                    label="Connect your bank"
                    testid="link-card-chooser-plaid-open"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-4 py-2 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled
              aria-disabled="true"
              data-testid="link-card-chooser-automatic"
              className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3 opacity-70 cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-xl bg-[#00E676]/10 border border-[#00E676]/20 flex items-center justify-center flex-shrink-0">
                {loading ? (
                  <Loader2 size={16} className="text-[#00E676] animate-spin" />
                ) : (
                  <Zap size={18} className="text-[#00E676]" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="text-sm font-semibold text-white"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    Automatic Sync
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-[#F5B841]/40 bg-[#F5B841]/[0.08] px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] text-[#F5B841] font-bold"
                    data-testid="link-card-chooser-coming-soon-badge"
                  >
                    <Lock size={9} /> Coming Soon
                  </span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Live balance + limit updates from your bank. We're finishing the last mile of production integration — check back soon.
                </p>
              </div>
            </button>
          )}

          {/* Manual entry — always active */}
          <button
            type="button"
            onClick={() => {
              onOpenChange(false);
              setTimeout(() => onChooseManual && onChooseManual(), 120);
            }}
            data-testid="link-card-chooser-manual"
            className="w-full text-left rounded-2xl border border-white/10 hover:border-white/40 bg-white/[0.02] hover:bg-white/[0.05] p-4 flex items-start gap-3 transition-colors focus:outline-none focus:ring-2 focus:ring-[#00E676]/40"
          >
            <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center flex-shrink-0">
              <PencilLine size={18} className="text-slate-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span
                  className="text-sm font-semibold text-white"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Add Card Manually
                </span>
                <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Enter the balance, credit limit and due date yourself. Updates instantly, no bank connection needed.
              </p>
            </div>
          </button>
        </div>

        <p className="mt-4 text-[11px] text-slate-400 text-center">
          {plaidEnabled
            ? "Your bank credentials never touch our servers. Plaid handles the connection."
            : "You can always switch to Automatic Sync later — nothing you enter now is lost."}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default LinkCardChooserDialog;
